import { IconButton } from "@carbon6/c6-frontend-design";
import { ResourceBrowserLayout, PaginationFooter } from "@/components/browse";
import { FilterResultCount } from "@/components/filter";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import { workerJobStatusLabel } from "./workerJobStatusLabel";
import { diagnosticJobKindLabel } from "./diagnosticJobKindLabel";
import { WorkerJobDetailModal } from "./WorkerJobDetailModal";
import { WorkUnitProgressCell } from "./WorkUnitProgressCell";
import { WorkerQueueGuide } from "./WorkerQueueGuide";
import { DiagnosticCapacityStrip } from "./DiagnosticCapacityStrip";
import { useDiagnosticJobsController } from "./useWorkerOperationsController";
import { WorkerTimeline } from "./WorkerTimeline";
import { jobTimeline } from "./workerTimelineModel";
import styles from "./DiagnosticJobsPanel.module.css";
import { tableStripError } from "@/components/table/tableStateMessages";

export function DiagnosticJobsPanel() {
  const { source, snapshot, selected, setSelected, now, queryKey } = useDiagnosticJobsController();
  const loading = snapshot.loadState === "loading";
  return (
    <>
      <ResourceBrowserLayout
        ariaLabel="관리자 진단 큐"
        summaryCount={snapshot.total}
        summaryLabel="진단 작업"
        summaryInFilter
        // 행이 없는 조회 실패는 타임라인 본문이 알린다. 띠에는 행이 남아 있는 실패만 올린다.
        error={tableStripError(snapshot)}
        onRetry={() => void source.refresh()}
        footer={
          <PaginationFooter
            page={snapshot.page}
            size={snapshot.size}
            total={snapshot.total}
            isLoading={loading}
            ariaLabel="진단 큐 페이지 이동"
            onPrevious={() => snapshot.setPage(snapshot.page - 1)}
            onNext={() => snapshot.setPage(snapshot.page + 1)}
          />
        }
      >
        <div className={styles.content}>
          <DiagnosticCapacityStrip capacity={source.capacity} onOpenJob={setSelected} />
          <WorkerTimeline
            ariaLabel="진단 큐 타임라인"
            now={now}
            rangeKey={queryKey}
            loadState={snapshot.loadState}
            errorMessage={snapshot.errorMessage}
            onRetry={() => void source.refresh()}
            emptyMessage="조건에 맞는 진단 작업이 없습니다."
            summary={<FilterResultCount count={snapshot.total} unitLabel="진단 작업" />}
            actions={
              <>
                <WorkerQueueGuide kind="diagnostic" />
                <IconButton
                  text="새로고침"
                  size="sm"
                  variant="surface"
                  disabled={loading}
                  onClick={() => void source.refresh()}
                />
              </>
            }
            rows={snapshot.rows.map(({ job }) => ({
              id: job.uuid,
              name: job.dataset_display_name,
              status: `${diagnosticJobKindLabel(job.kind)}${job.reset ? " · 강제" : ""} · ${workerJobStatusLabel(job.status)}`,
              summary: (
                <WorkUnitProgressCell
                  primary={`완료 ${job.completed_count} / ${job.requested_count} · 실패 ${job.failed_count}`}
                  units={job.units}
                />
              ),
              timeline: jobTimeline(job, now),
              onOpen: () => setSelected(job.uuid),
              action: (
                <IconButton
                  text="Dataset 상세 보기"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    openDatasetDetailWindow({
                      datasetUuid: job.dataset_uuid,
                      displayName: job.dataset_display_name,
                      initialTab: "review",
                    })
                  }
                />
              ),
            }))}
          />
        </div>
      </ResourceBrowserLayout>
      {selected !== null && (
        <WorkerJobDetailModal
          key={selected}
          kind="diagnostic"
          uuid={selected}
          onClose={() => setSelected(null)}
          onChanged={() => source.refresh()}
        />
      )}
    </>
  );
}
